import lodashGet from 'lodash/get';
import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import omit from 'lodash/omit';
import without from 'lodash/without';
import has from 'lodash/has';
import set from 'lodash/set';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import mapValues from 'lodash/mapValues';
import nanoid from 'nanoid';
import {
  defaultFormUploadField,
  defaultFormNumberField,
} from './default-fields/form-default-fields';
import {
  ELEMENT_DESCRIPTION_ID,
  ELEMENT_EMAIL_ID,
  TYPE_GROUP,
  TYPE_DATE_FIELD,
  TYPE_UPLOAD_FIELD,
  TYPE_SELECT_FIELD,
  TYPE_TAXONOMY_FIELD,
  TYPE_TEXT_FIELD,
  TYPE_NUMBER_FIELD,
  TEXTTYPE_TEXT,
  TEXTTYPE_EMAIL,
  TEXTTYPE_NUMBER,
  TYPE_RADIO_FIELD,
  MODE,
  TYPE_CHECKBOX_FIELD,
  DEFAULT_MAX_SELECTIONS,
  FIELD_LABEL_LIMIT,
} from '../../constants';
import messages from './messages';
import newFormElementMessages from './new-form-element/messages';
import getCurrentDate from '../../common/utils/getCurrentDate';

// @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
const get = (obj, path, defaultValue) =>
  cloneDeep(lodashGet(obj, path) || defaultValue);

const elementIdPrefix = 'element-';

const fieldDefaultLabelByType = {
  [TYPE_UPLOAD_FIELD]: messages.uploadLabel,
  [TYPE_NUMBER_FIELD]: messages.numberLabel,
  [TYPE_RADIO_FIELD]: messages.radioLabel,
  [TYPE_CHECKBOX_FIELD]: messages.checkboxLabel,
  [TYPE_SELECT_FIELD]: messages.selectLabel,
  [TYPE_DATE_FIELD]: messages.dateLabel,
  [TYPE_TAXONOMY_FIELD]: messages.taxonomyLabel,
  [TYPE_TEXT_FIELD]: (element = {}) => {
    // function to return the default label for a text field type based on element properties
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
    const { multiline = false, id } = element;
    if (id === ELEMENT_EMAIL_ID) {
      return messages.emailLabel;
    }
    if (id === ELEMENT_DESCRIPTION_ID) {
      return messages.descriptionLabel;
    }
    return multiline ? messages.longTextLabel : messages.shortTextLabel;
  },
} as const;

const getIntlDefaultLabel = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  elType: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  element: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  isTitleElement: any,
) => {
  if (isTitleElement) {
    return `${intl.formatMessage(
      messages.formDefaultTitle,
    )} ${getCurrentDate()}`;
  }
  if (elType === TYPE_TEXT_FIELD) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7053
    return intl.formatMessage(fieldDefaultLabelByType[elType](element));
  }
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7053
  if (fieldDefaultLabelByType[elType]) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7053
    return intl.formatMessage(fieldDefaultLabelByType[elType]);
  }
  return '';
};

const getIntlPlaceholder = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  elType: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  element: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  mode: any,
) => {
  if (elType === TYPE_GROUP) {
    return intl.formatMessage(newFormElementMessages.labelPlaceholderAddTitle);
  }
  if (mode === MODE.FILE_REQUEST) {
    return intl.formatMessage(newFormElementMessages.labelPlaceholderAddALabel);
  }
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  if (getIntlDefaultLabel(elType, element, intl)) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
    return getIntlDefaultLabel(elType, element, intl);
  }
  return intl.formatMessage(newFormElementMessages.labelPlaceholderAddALabel);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const defaultUploadField = (intl: any) => ({
  type: TYPE_UPLOAD_FIELD,
  label: intl.formatMessage(messages.uploadLabel),
  required: true,
});

const defaultRadioField = () => ({
  type: TYPE_RADIO_FIELD,
  label: '',
  required: true,
});

const defaultCheckboxField = () => ({
  type: TYPE_CHECKBOX_FIELD,
  label: '',
  required: true,
  maximumSelections: DEFAULT_MAX_SELECTIONS,
});

const defaultTextField = {
  type: TYPE_TEXT_FIELD,
  textType: TEXTTYPE_TEXT,
  required: true,
} as const;

const defaultShortTextField = () => ({
  ...defaultTextField,
  label: '',
});

const defaultLongTextField = () => ({
  ...defaultTextField,
  label: '',
  multiline: true,
});

const defaultNumberField = {
  type: TYPE_TEXT_FIELD,
  textType: TEXTTYPE_NUMBER,
  required: true,
} as const;

const defaultSelectField = () => ({
  type: TYPE_SELECT_FIELD,
  required: true,
  maximumSelections: DEFAULT_MAX_SELECTIONS,
  label: '',
  options: [],
});

const defaultSingleSelectField = {
  type: TYPE_SELECT_FIELD,
  multiSelect: false,
  required: true,
} as const;

const defaultMultiSelectField = {
  type: TYPE_SELECT_FIELD,
  multiSelect: true,
  required: true,
} as const;

const defaultTaxonomyField = {
  type: TYPE_TAXONOMY_FIELD,
  required: true,
} as const;

const defaultDateField = {
  type: TYPE_DATE_FIELD,
  required: true,
} as const;

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const defaultEmailField = (intl: any) => ({
  id: ELEMENT_EMAIL_ID,
  type: TYPE_TEXT_FIELD,
  textType: TEXTTYPE_EMAIL,
  label: intl.formatMessage(messages.emailLabel),
  visible: true,
  required: true,
});

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const defaultDescriptionField = (intl: any) => ({
  id: ELEMENT_DESCRIPTION_ID,
  type: TYPE_TEXT_FIELD,
  textType: TEXTTYPE_TEXT,
  multiline: true,
  label: intl.formatMessage(messages.descriptionLabel),
  visible: true,
  required: true,
});

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const isForm = (mode: any) => {
  return mode === MODE.FORM;
};

// @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
const assignUniqueIdToElement = newComponent => {
  const newUniqueKey = nanoid();
  return {
    ...newComponent,
    id: `${elementIdPrefix}${newUniqueKey}`,
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getMainGroupComponent = (existingComponents: any) =>
  cloneDeep(
    find(existingComponents, component => component.type === TYPE_GROUP),
  );

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */ // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
const addToComponents = (newComponent, existingComponents: any) => {
  const group = getMainGroupComponent(existingComponents);
  const items = [...without(group.items, newComponent.id), newComponent.id];
  return {
    ...existingComponents,
    [group.id]: { ...group, items },
    [newComponent.id]: newComponent,
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const addUploadField = (formContent: any, intl: any, mode: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const defaultField = isForm(mode)
    ? defaultFormUploadField()
    : defaultUploadField(intl);
  return {
    ...formContent,
    components: addToComponents(
      assignUniqueIdToElement(defaultField),
      existingComponents,
    ),
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const addRadioField = (formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  return {
    ...formContent,
    components: addToComponents(
      assignUniqueIdToElement(defaultRadioField()),
      existingComponents,
    ),
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const addCheckboxField = (formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  return {
    ...formContent,
    components: addToComponents(
      assignUniqueIdToElement(defaultCheckboxField()),
      existingComponents,
    ),
  };
};

const addEmailFieldWithUniqueId = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  emailField: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  existingComponents: any,
) => {
  const emailComponent = assignUniqueIdToElement(emailField);
  const formContentWithEmailField = {
    ...formContent,
    components: addToComponents(emailComponent, existingComponents),
  } as const;

  return {
    updatedFormContent: formContentWithEmailField,
    emailFieldId: emailComponent.id,
  };
};

const addEmailFieldWithFixedId = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  emailField: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  existingComponents: any,
) => {
  return {
    updatedFormContent: {
      ...formContent,
      components: {
        ...existingComponents,
        [ELEMENT_EMAIL_ID]: emailField,
      },
    },
    emailFieldId: ELEMENT_EMAIL_ID,
  };
};

const setEmailFieldVisible = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  visible: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  mode: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const emailField = get(
    existingComponents,
    ELEMENT_EMAIL_ID,
    defaultEmailField(intl),
  );
  set(emailField, 'visible', visible);
  set(emailField, 'required', visible);
  set(emailField, 'label', intl.formatMessage(messages.emailLabel));
  return mode === MODE.FORM
    ? addEmailFieldWithUniqueId(formContent, emailField, existingComponents)
    : addEmailFieldWithFixedId(formContent, emailField, existingComponents);
};

const setDescriptionFieldVisible = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  visible: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const descriptionField = get(
    existingComponents,
    ELEMENT_DESCRIPTION_ID,
    defaultDescriptionField(intl),
  );
  set(descriptionField, 'visible', visible);
  set(descriptionField, 'required', visible);
  set(descriptionField, 'label', intl.formatMessage(messages.descriptionLabel));
  return {
    ...formContent,
    components: {
      ...existingComponents,
      [ELEMENT_DESCRIPTION_ID]: descriptionField,
    },
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const addEmailField = (formContent: any, intl: any, mode: any) => {
  const { updatedFormContent, emailFieldId } = setEmailFieldVisible(
    formContent,
    true,
    intl,
    mode,
  );
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const group = getMainGroupComponent(get(formContent, 'components'));
  const items =
    mode === MODE.FORM
      ? [...group.items, emailFieldId]
      : [...without(group.items, ELEMENT_EMAIL_ID), ELEMENT_EMAIL_ID];

  return {
    ...updatedFormContent,
    components: {
      ...updatedFormContent.components,
      [group.id]: { ...group, items },
    },
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const addDescriptionField = (formContent: any, intl: any) => {
  const updatedFormContent = setDescriptionFieldVisible(
    formContent,
    true,
    intl,
  );
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const group = getMainGroupComponent(get(formContent, 'components'));
  const items = [
    ...without(group.items, ELEMENT_DESCRIPTION_ID),
    ELEMENT_DESCRIPTION_ID,
  ];

  return {
    ...updatedFormContent,
    components: {
      ...updatedFormContent.components,
      [group.id]: { ...group, items },
    },
  };
};

const getUploadFieldIds = (formContent = {}) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
  const { components } = formContent;
  return (
    filter(components, element => element.type === TYPE_UPLOAD_FIELD).map(
      component => component.id,
    ) || []
  );
};

// Figure out where this is being used?
// insertMetadataField is used instead and this should be deprecated
const addMetadataField = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  metadataField: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  metadataTemplateId: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  let field;
  switch (metadataField.type) {
    case 'date':
      field = { ...defaultDateField };
      break;
    case 'float':
      field = { ...defaultNumberField };
      break;
    case 'multiSelect':
      field = {
        ...defaultMultiSelectField,
        options: map(metadataField.options, options => options.key),
      };
      break;
    case 'enum':
      field = {
        ...defaultSingleSelectField,
        options: map(metadataField.options, options => options.key),
      };
      break;
    // Commented out on purpose - I assume the whole addMetadataField is a dead code
    // Looks like we're using insertMetadataField instead
    // case 'taxonomy':
    //   field = {
    //     ...defaultTaxonomyField,
    //     namespace: metadataField.namespace,
    //     optionsRules: metadataField.optionsRules,
    //     taxonomyKey: metadataField.taxonomyKey,
    //     isText: metadataField.isText,
    //   };
    //   break;
    case 'string':
    default:
      field = { ...defaultTextField };
      break;
  }

  field = {
    ...field,
    label: metadataField.displayName,
    description: metadataField.description,
    metadataFieldKey: metadataField.key,
    metadataTemplateId,
    uploadFieldIds: getUploadFieldIds(formContent),
  };

  return {
    ...formContent,
    components: addToComponents(
      assignUniqueIdToElement(field),
      existingComponents,
    ),
  };
};

const removeUploadFieldFromMetadata = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  uploadFieldIdToRemove: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const updatedComponents = mapValues(existingComponents, component => {
    if (Array.isArray(component?.uploadFieldIds)) {
      return {
        ...component,
        uploadFieldIds: without(
          component.uploadFieldIds,
          uploadFieldIdToRemove,
        ),
      };
    }
    return component;
  });

  return {
    ...formContent,
    components: updatedComponents,
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const removeField = (componentIdToRemove: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);
  return {
    ...formContent,
    components: {
      ...omit(existingComponents, componentIdToRemove),
      [group.id]: {
        ...group,
        items: without(group.items, componentIdToRemove),
      },
    },
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const toggleFieldRequired = (componentId: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const component = existingComponents[componentId];
  return {
    ...formContent,
    components: {
      ...existingComponents,
      [componentId]: { ...component, required: !component.required },
    },
  };
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */ // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
const insertField = (group, index: any, formContent: any, newId) => {
  const { items = [] } = group;
  items.splice(index, 0, newId);
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
  const filteredItems = items.filter((item, i) => {
    if (i !== index && item === newId) {
      return false;
    }
    return true;
  });
  return {
    ...formContent,
    components: {
      ...formContent.components,
      [group.id]: { ...group, items: filteredItems },
    },
  };
};

const insertDuplicateField = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  index: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  duplicateField: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);
  let newLabel = duplicateField.label;
  if (duplicateField.label?.length === FIELD_LABEL_LIMIT) {
    newLabel = `${duplicateField.label.slice(0, FIELD_LABEL_LIMIT - 7)}`;
  }
  duplicateField.label =
    newLabel &&
    intl.formatMessage(messages.copyLabel, {
      label: newLabel,
    });
  const newComponent = assignUniqueIdToElement(duplicateField);
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

const insertUploadField = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  index: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  mode: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);
  const defaultField = isForm(mode)
    ? defaultFormUploadField()
    : defaultUploadField(intl);
  const newComponent = assignUniqueIdToElement(defaultField);
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertRadioField = (index: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);

  const newComponent = assignUniqueIdToElement(defaultRadioField());
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertCheckboxField = (index: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);

  const newComponent = assignUniqueIdToElement(defaultCheckboxField());
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertShortTextField = (index: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);

  const newComponent = assignUniqueIdToElement(defaultShortTextField());
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertLongTextField = (index: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);

  const newComponent = assignUniqueIdToElement(defaultLongTextField());
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertDescriptionField = (index: any, formContent: any, intl: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);

  const updatedFormContent = setDescriptionFieldVisible(
    formContent,
    true,
    intl,
  );

  return insertField(group, index, updatedFormContent, ELEMENT_DESCRIPTION_ID);
};

const insertEmailField = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  index: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  intl: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  mode: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);

  const { emailFieldId, updatedFormContent } = setEmailFieldVisible(
    formContent,
    true,
    intl,
    mode,
  );

  return insertField(group, index, updatedFormContent, emailFieldId);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertSelectField = (index: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);
  const newComponent = assignUniqueIdToElement(defaultSelectField());
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const insertNumberField = (index: any, formContent: any) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);
  const defaultField = defaultFormNumberField();
  const newComponent = assignUniqueIdToElement(defaultField);
  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;
  return insertField(group, index, updatedFormContent, newComponent.id);
};

const insertMetadataField = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  index: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  metadataField: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  metadataTemplateId: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  metadataTemplate: any,
) => {
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2554
  const existingComponents = get(formContent, 'components');
  const group = getMainGroupComponent(existingComponents);
  let field;
  switch (metadataField.type) {
    case 'date':
      field = { ...defaultDateField };
      break;
    case 'float':
      field = { ...defaultNumberField };
      break;
    case 'multiSelect':
      field = {
        ...defaultMultiSelectField,
        options: map(metadataField.options, options => options.key),
      };
      break;
    case 'enum':
      field = {
        ...defaultSingleSelectField,
        options: map(metadataField.options, options => options.key),
      };
      break;
    case 'taxonomy':
      field = {
        ...defaultTaxonomyField,
        namespace: metadataField.namespace,
        optionsRules: metadataField.optionsRules,
        taxonomyKey: metadataField.taxonomyKey,
        isText: metadataField.isText,
      };
      break;
    case 'string':
    default:
      field = { ...defaultTextField };
      break;
  }

  field = {
    ...field,
    label: metadataField.displayName,
    description: metadataField.description,
    metadataFieldKey: metadataField.key,
    metadataTemplateId,
    metadataTemplateKey: metadataTemplate?.templateKey,
    scope: metadataTemplate?.scope,
    uploadFieldIds: getUploadFieldIds(formContent),
  };

  const newComponent = assignUniqueIdToElement(field);

  const updatedFormContent = {
    ...formContent,
    components: {
      ...formContent.components,
      [newComponent.id]: newComponent,
    },
  } as const;

  return insertField(group, index, updatedFormContent, newComponent.id);
};

const insertMetadataTemplate = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  index: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  formContent: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  metadataTemplate: any,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  prevMetadataTemplate: any,
) => {
  const newTemplateId = metadataTemplate.id;
  const newMetadataFields = filter(metadataTemplate.fields, field => {
    if (prevMetadataTemplate) {
      const prevField = prevMetadataTemplate.fields.find(
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
        field2 => field2.id === field.id,
      );
      if (has(prevField, 'isSelected') && prevField.isSelected) {
        return false;
      }
    }
    return field.isSelected;
  });
  let updatedFormContent = formContent;
  let insertIndex = index;
  forEach(newMetadataFields, metadataField => {
    updatedFormContent = insertMetadataField(
      insertIndex,
      updatedFormContent,
      metadataField,
      newTemplateId,
      metadataTemplate,
    );
    insertIndex += 1;
  });
  return updatedFormContent;
};

export {
  addUploadField,
  addRadioField,
  addCheckboxField,
  addEmailField,
  addDescriptionField,
  setEmailFieldVisible,
  setDescriptionFieldVisible,
  addMetadataField,
  removeUploadFieldFromMetadata,
  removeField,
  toggleFieldRequired,
  insertShortTextField,
  insertLongTextField,
  insertUploadField,
  insertRadioField,
  insertCheckboxField,
  insertDescriptionField,
  insertEmailField,
  insertMetadataTemplate,
  insertSelectField,
  insertDuplicateField,
  insertMetadataField,
  insertNumberField,
  fieldDefaultLabelByType,
  getIntlDefaultLabel,
  getIntlPlaceholder,
};
